(function giftMessagePersistenceModule(global) {
  'use strict';

  const GIFT_NOTE_KEYS = ['Gift Note To', 'Gift Note Message', 'Gift Note From'];
  const GIFT_NOTE_LIMITS = {
    'Gift Note To': 100,
    'Gift Note Message': 240,
    'Gift Note From': 100
  };

  const emptySnapshot = () => GIFT_NOTE_KEYS.reduce((snapshot, key) => {
    snapshot[key] = '';
    return snapshot;
  }, {});

  const copySnapshot = (snapshot) => GIFT_NOTE_KEYS.reduce((copy, key) => {
    copy[key] = String(snapshot && snapshot[key] || '').slice(0, GIFT_NOTE_LIMITS[key]);
    return copy;
  }, {});

  const snapshotsMatch = (first, second) => GIFT_NOTE_KEYS.every(
    (key) => String(first && first[key] || '') === String(second && second[key] || '')
  );

  class GiftMessagePersistenceController {
    constructor(options = {}) {
      this.document = options.document || global.document || null;
      this.fetch = options.fetch || (global.fetch ? global.fetch.bind(global) : null);
      this.setTimeout = options.setTimeout || global.setTimeout.bind(global);
      this.clearTimeout = options.clearTimeout || global.clearTimeout.bind(global);
      this.debounceMs = options.debounceMs == null ? 300 : options.debounceMs;
      this.retryLimit = options.retryLimit == null ? 2 : options.retryLimit;
      this.config = Object.assign({}, options.config || {});
      this.fields = new Set();
      this.fieldByElement = new WeakMap();
      this.checkoutBypass = new WeakSet();
      this.currentSnapshot = emptySnapshot();
      this.confirmedSnapshot = emptySnapshot();
      this.currentRevision = 0;
      this.confirmedRevision = 0;
      this.hasSnapshot = false;
      this.cardExists = false;
      this.operationTail = Promise.resolve();
      this.saveLoopPromise = null;
      this.debounceTimer = null;
      this.savedResetTimer = null;
      this.status = 'idle';
      this.lastError = null;
      this.started = false;
      this.checkoutInProgress = false;
      this.pendingCheckout = null;
    }

    configure(config = {}) {
      this.config = Object.assign({}, this.config, config);
      return this;
    }

    start() {
      if (this.started || !this.document) return;
      this.started = true;

      const ready = () => {
        this.register(this.document);
        this.bindDialog();
        this.observeDynamicFields();
        this.normalizeAndRefresh().catch((error) => {
          console.error('Error normalizing gift products:', error); // eslint-disable-line no-console
        });
      };

      if (this.document.readyState === 'loading') {
        this.document.addEventListener('DOMContentLoaded', ready, { once: true });
      } else {
        ready();
      }

      this.document.addEventListener('on:cart:add', () => {
        this.register(this.document);
        this.normalizeAndRefresh().catch((error) => {
          console.error('Error normalizing gift products:', error); // eslint-disable-line no-console
        });
      });
      this.document.addEventListener('on:cart:change', (event) => {
        if (event.detail && event.detail.giftProductsNormalized) return;
        this.normalizeAndRefresh().catch((error) => {
          console.error('Error normalizing gift products:', error); // eslint-disable-line no-console
        });
      });
      this.document.addEventListener('on:cart:after-merge', () => {
        this.register(this.document);
        if (this.cardExists && this.hasSnapshot) this.updatePreviews(this.confirmedSnapshot);
      });
      this.document.addEventListener('change', this.handleGiftOptionChange.bind(this));
      this.document.addEventListener('click', this.handleCheckoutClick.bind(this), true);
      this.document.addEventListener('submit', this.handleCheckoutSubmit.bind(this), true);
      this.document.addEventListener('click', (event) => {
        const opener = event.target.closest && event.target.closest('[data-gift-message-edit]');
        if (!opener) return;
        this.editGreeting(opener);
      });
    }

    observeDynamicFields() {
      if (!global.MutationObserver || !this.document.documentElement) return;
      this.observer = new global.MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) this.register(node);
          });
        });
      });
      this.observer.observe(this.document.documentElement, { childList: true, subtree: true });
    }

    register(root) {
      if (!root || !root.querySelectorAll) return;
      this.fields.forEach((field) => {
        if (field.element.isConnected === false) this.fields.delete(field);
      });
      const elements = [];
      if (root.matches && root.matches('[data-gift-message-fieldset]')) elements.push(root);
      root.querySelectorAll('[data-gift-message-fieldset]').forEach((element) => elements.push(element));
      elements.forEach((element) => this.registerField(element));
    }

    registerField(element) {
      if (this.fieldByElement.has(element)) return this.fieldByElement.get(element);

      const inputs = new Map();
      element.querySelectorAll('[data-gift-message-key]').forEach((input) => {
        if (GIFT_NOTE_KEYS.includes(input.dataset.giftMessageKey)) {
          inputs.set(input.dataset.giftMessageKey, input);
        }
      });
      if (!inputs.size) return null;

      const field = {
        element,
        inputs,
        button: element.querySelector('[data-gift-message-save]'),
        label: element.querySelector('[data-gift-message-save-label]'),
        errorMessage: element.querySelector('[data-gift-message-error]'),
        cardExists: element.dataset.giftCardPresent === 'true'
      };
      this.fields.add(field);
      this.fieldByElement.set(element, field);

      inputs.forEach((input) => {
        input.maxLength = GIFT_NOTE_LIMITS[input.dataset.giftMessageKey];
        input.addEventListener('input', () => this.handleInput(field));
      });
      if (field.button && !element.closest('[data-gift-message-dialog]')) {
        field.button.addEventListener('click', () => this.handleManualSave(field));
      }

      const snapshot = this.snapshotFromField(field);
      if (field.cardExists) {
        this.cardExists = true;
        if (!this.hasSnapshot) {
          this.currentSnapshot = snapshot;
          this.confirmedSnapshot = copySnapshot(snapshot);
          this.confirmedRevision = this.currentRevision;
          this.hasSnapshot = true;
        } else {
          this.applySnapshot(field, this.currentSnapshot);
        }
      }
      this.renderFieldStatus(field);
      this.updateCounter(field);
      return field;
    }

    getFieldsWithin(root) {
      this.register(root);
      return Array.from(this.fields).filter((field) => (
        field.element === root || (root && root.contains && root.contains(field.element))
      ));
    }

    snapshotFromField(field) {
      const snapshot = emptySnapshot();
      field.inputs.forEach((input, key) => {
        snapshot[key] = String(input.value || '').slice(0, GIFT_NOTE_LIMITS[key]);
      });
      return snapshot;
    }

    applySnapshot(field, snapshot) {
      field.inputs.forEach((input, key) => {
        input.value = String(snapshot[key] || '').slice(0, GIFT_NOTE_LIMITS[key]);
      });
      this.updateCounter(field);
    }

    updateCounter(field) {
      const counter = field.element.querySelector('[data-gift-message-count]');
      if (counter) counter.textContent = String((field.inputs.get('Gift Note Message')?.value || '').length);
    }

    capture(root) {
      const fields = this.getFieldsWithin(root).filter((field) => (
        field.cardExists || Array.from(field.inputs.values()).some((input) => !input.disabled)
      ));
      const activeElement = this.document && this.document.activeElement;
      const field = fields.find((candidate) => activeElement && candidate.element.contains(activeElement))
        || fields[0];
      if (!field) return { revision: this.currentRevision, snapshot: copySnapshot(this.currentSnapshot) };
      this.setCurrentSnapshot(this.snapshotFromField(field));
      return { revision: this.currentRevision, snapshot: copySnapshot(this.currentSnapshot) };
    }

    setCurrentSnapshot(snapshot) {
      const next = copySnapshot(snapshot);
      if (!this.hasSnapshot || !snapshotsMatch(next, this.currentSnapshot)) {
        this.currentSnapshot = next;
        this.currentRevision += 1;
        this.hasSnapshot = true;
      }
      return this.currentRevision;
    }

    handleInput(field) {
      field.inputs.forEach((input) => input.setCustomValidity?.(''));
      this.updateCounter(field);
      this.setCurrentSnapshot(this.snapshotFromField(field));
      this.fields.forEach((otherField) => {
        if (otherField !== field && otherField.cardExists) this.applySnapshot(otherField, this.currentSnapshot);
      });

      if (this.cardExists || field.cardExists) {
        this.cardExists = true;
        this.setStatus('saving');
        this.scheduleSave();
      }
    }

    handleManualSave(field) {
      if (!this.validateField(field)) return;
      this.setCurrentSnapshot(this.snapshotFromField(field));
      this.requestSave().catch(() => {});
    }

    handleGiftOptionChange(event) {
      const checkbox = event.target && event.target.closest
        ? event.target.closest('[data-gift-option-checkbox]')
        : null;
      if (!checkbox) return;

      const wrapper = checkbox.closest('[data-gift-option]');
      const form = checkbox.closest('form');
      if (!wrapper || !form) return;
      const index = wrapper.dataset.giftIndex;
      const idInput = form.querySelector(`[data-gift-option-id][data-gift-index="${index}"]`);
      wrapper.classList.toggle('checked', checkbox.checked);
      if (idInput) idInput.disabled = !checkbox.checked;

      if (index !== '1') return;
      const element = form.querySelector('[data-gift-message-fieldset]');
      if (!element) return;
      const field = this.registerField(element);
      element.hidden = !checkbox.checked;
      field.inputs.forEach((input) => {
        input.required = checkbox.checked;
        input.disabled = !checkbox.checked;
        if (!checkbox.checked) input.value = '';
      });
      if (!checkbox.checked && field.cardExists) this.handleInput(field);
    }

    validateField(field) {
      field.inputs.forEach((input) => {
        input.setCustomValidity?.(String(input.value || '').trim() ? '' : (
          this.dialog?.dataset.missingLabel || input.validationMessage || 'Please fill out this field.'
        ));
      });
      return Array.from(field.inputs.values()).every((input) => (
        typeof input.reportValidity !== 'function' || input.reportValidity()
      ));
    }

    async markCardAdded(root) {
      // An add can finish while an earlier normalization is still reading the cart.
      // Publish the confirmed card presence after that operation, never before it.
      await this.enqueueOperation(() => {
        const fields = this.getFieldsWithin(root);
        if (!this.hasSnapshot) this.capture(root);
        fields.forEach((field) => {
          field.cardExists = true;
          field.element.dataset.giftCardPresent = 'true';
          field.element.querySelector('[data-gift-message-actions]')?.removeAttribute('hidden');
          this.applySnapshot(field, this.currentSnapshot);
        });
        this.cardExists = true;
      });
      return this.requestSave();
    }

    scheduleSave() {
      if (!this.cardExists) return;
      if (this.debounceTimer) this.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      // The active drain already owns subsequent revisions; do not leave a
      // debounce timer that could restart an exhausted retry cycle.
      if (this.saveLoopPromise) return;
      this.debounceTimer = this.setTimeout(() => {
        this.debounceTimer = null;
        this.requestSave().catch(() => {});
      }, this.debounceMs);
    }

    requestSave() {
      if (!this.cardExists) return Promise.resolve({ skipped: true });
      if (this.debounceTimer) {
        this.clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      if (!this.hasSnapshot) return Promise.resolve({ skipped: true });
      if (this.saveLoopPromise) return this.saveLoopPromise;
      if (snapshotsMatch(this.currentSnapshot, this.confirmedSnapshot)
        && this.currentRevision === this.confirmedRevision) {
        this.setStatus('saved');
        return Promise.resolve({ snapshot: copySnapshot(this.confirmedSnapshot) });
      }
      this.saveLoopPromise = this.drainSaves()
        .finally(() => { this.saveLoopPromise = null; });
      return this.saveLoopPromise;
    }

    async drainSaves() {
      this.lastError = null;
      this.setStatus('saving');
      let savedCart;

      while (!snapshotsMatch(this.currentSnapshot, this.confirmedSnapshot)
        || this.currentRevision !== this.confirmedRevision) {
        const submittedSnapshot = copySnapshot(this.currentSnapshot);
        const submittedRevision = this.currentRevision;
        let saved = false;
        let error;

        for (let attempt = 0; attempt <= this.retryLimit; attempt += 1) {
          try {
            const result = await this.enqueueOperation(() => (
              this.cardExists ? this.writeSnapshot(submittedSnapshot) : { skipped: true }
            ));
            if (result && result.skipped) {
              this.setStatus('idle');
              return result;
            }
            savedCart = result;
            saved = true;
            break;
          } catch (requestError) {
            error = requestError;
            if (!snapshotsMatch(submittedSnapshot, this.currentSnapshot)
              || submittedRevision !== this.currentRevision) break;
          }
        }

        if (saved) {
          this.confirmedSnapshot = submittedSnapshot;
          this.confirmedRevision = submittedRevision;
          continue;
        }

        if (!snapshotsMatch(submittedSnapshot, this.currentSnapshot)
          || submittedRevision !== this.currentRevision) continue;

        this.lastError = error || new Error('Unable to save greeting');
        this.setStatus('error');
        throw this.lastError;
      }

      this.setStatus('saved');
      return { revision: this.confirmedRevision, snapshot: copySnapshot(this.confirmedSnapshot), cart: savedCart };
    }

    async writeSnapshot(snapshot) {
      if (!this.fetch) throw new Error('Fetch is unavailable');
      const response = await this.fetch(this.cartUpdateUrl(), {
        method: 'POST',
        signal: global.AbortSignal?.timeout?.(10000),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes: copySnapshot(snapshot) })
      });
      if (!response.ok) throw new Error(`Failed to save greeting: ${response.status}`);
      const cart = await response.json();
      if (!snapshotsMatch(snapshot, cart.attributes)) throw new Error('Shopify did not confirm the saved greeting');
      this.updatePreviews(cart.attributes);
      return cart;
    }

    enqueueOperation(operation) {
      const queued = this.operationTail.catch(() => {}).then(operation);
      this.operationTail = queued;
      return queued;
    }

    async flush() {
      if (this.debounceTimer) {
        this.clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      if (!this.cardExists || !this.hasSnapshot) {
        await this.operationTail.catch(() => {});
        return { skipped: true };
      }

      let savedCart;
      while (this.cardExists && (!snapshotsMatch(this.currentSnapshot, this.confirmedSnapshot)
        || this.currentRevision !== this.confirmedRevision || this.saveLoopPromise)) {
        const saved = await this.requestSave();
        if (saved.cart) savedCart = saved.cart;
      }
      if (!this.cardExists) return { skipped: true };
      return { revision: this.confirmedRevision, snapshot: copySnapshot(this.confirmedSnapshot), cart: savedCart };
    }

    setStatus(status) {
      this.status = status;
      if (this.savedResetTimer) {
        this.clearTimeout(this.savedResetTimer);
        this.savedResetTimer = null;
      }
      this.fields.forEach((field) => this.renderFieldStatus(field));

      if (status === 'saved') {
        this.savedResetTimer = this.setTimeout(() => {
          if (snapshotsMatch(this.currentSnapshot, this.confirmedSnapshot)
            && this.currentRevision === this.confirmedRevision) {
            this.status = 'idle';
            this.fields.forEach((field) => this.renderFieldStatus(field));
          }
        }, 2000);
      }
    }

    renderFieldStatus(field) {
      if (!field.button || !field.label) return;
      const labels = {
        idle: field.button.dataset.saveLabel,
        saving: field.button.dataset.savingLabel,
        saved: field.button.dataset.savedLabel,
        error: field.button.dataset.errorLabel
      };
      field.label.textContent = labels[this.status] || labels.idle || '';
      if (field.errorMessage) {
        field.errorMessage.hidden = this.status !== 'error';
        field.errorMessage.textContent = this.status === 'error' ? labels.error : '';
        if (this.status === 'error') field.label.textContent = labels.idle;
      }
      field.button.disabled = (this.status === 'saving' && Boolean(this.saveLoopPromise))
        || (field === this.dialogField && Boolean(this.dialogSaving));
    }

    stripGreetingAttributes(formData) {
      GIFT_NOTE_KEYS.forEach((key) => {
        formData.delete(`attributes[${key}]`);
        formData.delete(`items[][properties][${key}]`);
      });
      return formData;
    }

    async normalize(options = {}) {
      return this.enqueueOperation(async () => {
        const giftProductIds = new Set((this.config.giftProductIds || []).map(Number));
        const cardProductId = Number(this.config.cardProductId || 0);
        if (!giftProductIds.size) return null;
        const cart = await this.getCart();

        const updates = {};
        const attributes = cart && cart.attributes ? cart.attributes : {};
        const hasOtherProducts = (cart.items || []).some((item) => (
          !giftProductIds.has(Number(item.product_id)) && Number(item.quantity) > 0
        ));
        let hasCardProduct = false;
        let hasKeptCardLine = false;

        (cart.items || []).forEach((item) => {
          if (!giftProductIds.has(Number(item.product_id))) return;
          if (!hasOtherProducts) {
            updates[item.key] = 0;
            return;
          }
          if (Number(item.product_id) === cardProductId) {
            if (hasKeptCardLine) {
              updates[item.key] = 0;
              return;
            }
            hasKeptCardLine = true;
            hasCardProduct = true;
          }
          if (item.quantity > 1) updates[item.key] = 1;
        });

        this.cardExists = hasCardProduct;
        if (!hasCardProduct) {
          // The cart no longer confirms the previous greeting. Keep the draft,
          // but force a fresh write if a card is added again with the same text.
          this.confirmedSnapshot = emptySnapshot();
          this.confirmedRevision = -1;
          this.fields.forEach((field) => {
            field.cardExists = false;
            field.element.dataset.giftCardPresent = 'false';
          });
        }

        const updatePayload = {};
        if (Object.keys(updates).length) updatePayload.updates = updates;
        const hasGiftAttributes = GIFT_NOTE_KEYS.some((key) => attributes[key]);
        if (!hasCardProduct && hasGiftAttributes) {
          updatePayload.attributes = GIFT_NOTE_KEYS.reduce((result, key) => {
            result[key] = null;
            return result;
          }, {});
        } else if (hasCardProduct) {
          const cardLine = (cart.items || []).find((item) => Number(item.product_id) === cardProductId);
          const properties = cardLine && cardLine.properties ? cardLine.properties : {};
          const migrated = {};
          GIFT_NOTE_KEYS.forEach((key) => {
            // A locally edited empty value is intentional, not a migration gap.
            if (this.currentRevision === 0 && !attributes[key] && properties[key]) {
              migrated[key] = properties[key];
            }
          });
          if (Object.keys(migrated).length) updatePayload.attributes = migrated;
        }

        let normalizedCart = cart;
        let changed = false;
        if (Object.keys(updatePayload).length) {
          normalizedCart = await this.cartRequest(this.cartUpdateUrl(), updatePayload, 'update cart');
          changed = true;
        }

        const cardLine = (normalizedCart.items || []).find(
          (item) => Number(item.product_id) === cardProductId
        );
        const lineProperties = cardLine && cardLine.properties ? Object.assign({}, cardLine.properties) : {};
        const hasLineGiftNotes = GIFT_NOTE_KEYS.some((key) => Object.prototype.hasOwnProperty.call(lineProperties, key));
        if (cardLine && hasLineGiftNotes) {
          GIFT_NOTE_KEYS.forEach((key) => delete lineProperties[key]);
          normalizedCart = await this.cartRequest(this.cartChangeUrl(), {
            id: cardLine.key,
            quantity: cardLine.quantity,
            properties: lineProperties
          }, 'remove line-level gift notes');
          changed = true;
        }

        if (hasCardProduct && (!this.hasSnapshot || (snapshotsMatch(this.currentSnapshot, this.confirmedSnapshot)
          && this.currentRevision === this.confirmedRevision))) {
          this.currentSnapshot = copySnapshot(normalizedCart.attributes);
          this.confirmedSnapshot = copySnapshot(this.currentSnapshot);
          this.hasSnapshot = true;
          this.fields.forEach((field) => {
            if (field.cardExists) this.applySnapshot(field, this.currentSnapshot);
          });
        }
        this.updatePreviews(normalizedCart.attributes);
        if (changed && options.refresh && this.document) {
          this.document.dispatchEvent(new global.CustomEvent('on:cart:change', {
            bubbles: true,
            cancelable: false,
            detail: { giftProductsNormalized: true }
          }));
        }
        return normalizedCart;
      });
    }

    async normalizeAndRefresh() {
      return this.normalize({ refresh: true });
    }

    async getCart() {
      if (!this.fetch) throw new Error('Fetch is unavailable');
      const response = await this.fetch(this.cartUrl(), {
        method: 'GET',
        signal: global.AbortSignal?.timeout?.(10000),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    }

    async cartRequest(url, payload, description) {
      const response = await this.fetch(url, {
        method: 'POST',
        signal: global.AbortSignal?.timeout?.(10000),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to ${description}: ${response.status}`);
      return response.json();
    }

    cartRoot() {
      return this.config.cartRoot || (global.Shopify && global.Shopify.routes.root) || '/';
    }

    cartUrl() {
      return this.config.cartUrl || `${this.cartRoot()}cart.js`;
    }

    cartUpdateUrl() {
      return this.config.cartUpdateUrl || (global.theme && global.theme.routes.cartUpdate)
        || `${this.cartRoot()}cart/update.js`;
    }

    cartChangeUrl() {
      return this.config.cartChangeUrl || `${this.cartRoot()}cart/change.js`;
    }

    isCheckoutTarget(target) {
      if (!target || !target.closest) return null;
      return target.closest('[data-gift-message-checkout], #cartform [name="checkout"], .additional-checkout-buttons input, a[href*="/checkout"]');
    }

    bindDialog() {
      if (this.dialog || !this.document.querySelector) return;
      this.dialog = this.document.querySelector('[data-gift-message-dialog]');
      if (!this.dialog) return;
      this.dialogForm = this.dialog.querySelector('[data-gift-message-dialog-form]');
      this.dialogField = this.registerField(this.dialog.querySelector('[data-gift-message-fieldset]'));
      this.dialogNotice = this.dialog.querySelector('[data-gift-message-notice]');
      const cancelDialog = () => {
        this.pendingCheckout = null;
        this.dialog.close();
      };
      this.dialog.querySelector('[data-gift-message-close]').addEventListener('click', cancelDialog);
      this.dialog.addEventListener('cancel', () => { this.pendingCheckout = null; });
      this.dialog.addEventListener('close', () => {
        this.pendingCheckout = null;
        this.dialogField.cardExists = false;
        if (this.dialogOpener?.isConnected) this.dialogOpener.focus();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) cancelDialog();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled])')];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && this.document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && this.document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      this.dialogForm.addEventListener('submit', (event) => this.saveDialog(event));
    }

    updatePreviews(snapshot) {
      this.document?.querySelectorAll('[data-gift-message-preview]').forEach((element) => {
        element.textContent = GIFT_NOTE_KEYS.every((key) => String(snapshot?.[key] || '').trim())
          ? GIFT_NOTE_KEYS.map((key) => snapshot[key]).join('\n') : element.dataset.missingLabel;
      });
    }

    cartHasCard(cart) {
      const cardId = Number(this.config.cardProductId);
      return cardId > 0 && (cart?.items || []).some((item) => Number(item.product_id) === cardId);
    }

    hasCompleteGreeting(cart) {
      return !this.cartHasCard(cart) || GIFT_NOTE_KEYS.every((key) => String(cart.attributes?.[key] || '').trim());
    }

    async verifiedCart() {
      await this.flush();
      const normalizedCart = await this.normalize();
      const saved = await this.flush();
      // Normalization reads Shopify afresh; a later save returns the newer cart.
      // Both responses confirm server state without a redundant second GET.
      const cart = saved.cart || normalizedCart || await this.enqueueOperation(() => this.getCart());
      this.cardExists = this.cartHasCard(cart);
      return cart;
    }

    showDialog(opener, { checkout = null, error = false, missing = false, cart = null } = {}) {
      this.bindDialog();
      if (!this.dialog) return;
      this.dialogOpener = opener;
      this.pendingCheckout = checkout;
      const field = this.dialogField;
      field.cardExists = this.cardExists;
      field.element.dataset.giftCardPresent = String(this.cardExists);
      this.applySnapshot(field, this.hasSnapshot ? this.currentSnapshot : copySnapshot(cart?.attributes));
      field.inputs.forEach((input) => {
        input.disabled = !this.cardExists;
        input.required = this.cardExists;
        input.setCustomValidity?.('');
      });
      field.button.dataset.saveLabel = checkout ? this.dialog.dataset.continueLabel : this.dialog.dataset.saveLabel;
      this.setStatus('idle');
      this.dialogNotice.hidden = !error && !missing;
      this.dialogNotice.textContent = error ? this.dialog.dataset.errorLabel : missing ? this.dialog.dataset.missingLabel : '';
      if (!this.dialog.open) this.dialog.showModal();
      const firstEmpty = [...field.inputs.values()].find((input) => !String(input.value || '').trim());
      (firstEmpty || field.inputs.get('Gift Note Message')).focus();
    }

    async editGreeting(opener) {
      try {
        await this.normalize();
        this.showDialog(opener);
      } catch (error) {
        this.lastError = error;
        this.showDialog(opener, { error: true });
      }
    }

    async saveDialog(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.dialogSaving || !this.validateField(this.dialogField)) return;
      this.dialogSaving = true;
      this.renderFieldStatus(this.dialogField);
      const pending = this.pendingCheckout;
      try {
        if (this.cardExists) this.setCurrentSnapshot(this.snapshotFromField(this.dialogField));
        if (pending) {
          await this.gateCheckout(event, pending.target, pending.continueCheckout, pending);
        } else {
          const cart = await this.verifiedCart();
          if (!this.hasCompleteGreeting(cart)) throw new Error('Greeting is incomplete');
          this.updatePreviews(cart.attributes);
          this.dialog.close();
        }
      } catch (error) {
        this.lastError = error;
        this.setStatus('error');
        this.dialogNotice.hidden = false;
        this.dialogNotice.textContent = this.dialog.dataset.errorLabel;
      } finally {
        this.dialogSaving = false;
        this.renderFieldStatus(this.dialogField);
      }
    }

    async handleCheckoutClick(event) {
      const target = this.isCheckoutTarget(event.target);
      if (!target || this.checkoutBypass.has(target)) return;

      const cartForm = target.closest('#cartform');
      const terms = cartForm && cartForm.querySelector('terms-agreement input[type="checkbox"]');
      if (terms && !terms.checked) return;

      return this.gateCheckout(event, target, () => target.click());
    }

    async handleCheckoutSubmit(event) {
      const form = event.target;
      const submitter = event.submitter;
      if (!form || this.checkoutBypass.has(form) || (submitter && this.checkoutBypass.has(submitter))) return;
      const isCheckout = (submitter && submitter.name === 'checkout')
        || (form.getAttribute && /\/checkout(?:[/?#]|$)/.test(form.getAttribute('action') || ''));
      if (!isCheckout) return;

      return this.gateCheckout(event, form, () => form.requestSubmit(submitter || undefined));
    }

    async gateCheckout(event, target, continueCheckout, expectedCheckout = null) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.checkoutInProgress) return;
      this.checkoutInProgress = true;

      try {
        this.register(this.document);
        const cart = await this.verifiedCart();
        if (expectedCheckout && this.pendingCheckout !== expectedCheckout) return;
        if (!this.hasCompleteGreeting(cart)) {
          // A reload or another cart writer can invalidate a previously saved draft.
          // The next save must write it again, even if the customer keeps the same text.
          this.confirmedSnapshot = copySnapshot(cart.attributes);
          this.confirmedRevision = -1;
          this.showDialog(target, { checkout: { target, continueCheckout }, missing: true, cart });
          return;
        }
        this.updatePreviews(cart.attributes);
        if (this.dialog?.open) this.dialog.close();
        this.checkoutBypass.add(target);
        continueCheckout();
        this.setTimeout(() => this.checkoutBypass.delete(target), 0);
      } catch (error) {
        if (expectedCheckout && this.pendingCheckout !== expectedCheckout) return;
        this.lastError = error;
        this.setStatus('error');
        this.showDialog(target, { checkout: { target, continueCheckout }, error: true });
      } finally {
        this.checkoutInProgress = false;
      }
    }
  }

  const exports = {
    GiftMessagePersistenceController,
    GIFT_NOTE_KEYS,
    GIFT_NOTE_LIMITS,
    snapshotsMatch
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = exports;
  global.GiftMessagePersistenceController = GiftMessagePersistenceController;

  if (global.document) {
    global.giftMessagePersistence = global.giftMessagePersistence
      || new GiftMessagePersistenceController({ config: global.GiftMessagePersistenceConfig || {} });
    global.giftMessagePersistence.start();
  }
}(typeof window !== 'undefined' ? window : globalThis));
